import React, { useEffect } from 'react';

import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { isEqual, merge } from 'lodash-es';
import PropTypes from 'prop-types';

import * as ScenarioStore from '@asteria/datalayer/stores/scenarios';

import { formatResponse, validateFormValues } from '../utils';

function isEmptyForm(getValues) {
	return (
		!Object.keys(getValues()).length ||
		!['deposit', 'withdraw'].reduce(
			(acc, type) =>
				Object.values(getValues([type, 'data'].join('.')) ?? {})
					.flatMap((value) => Object.values(value))
					.reduce((acc, { value }) => acc + (value || 0), 0),
			{},
		)
	);
}

function isEmptyData(data) {
	return !Object.keys(data ?? {}).length;
}

function useUpdate(query) {
	const { setValue, getValues } = useFormContext();

	const sourceScenarioData = useSelector(
		ScenarioStore.selectors.sourceScenarioData,
		isEqual,
	);

	useEffect(() => {
		if (isEmptyData(query?.data) || isEmptyForm(getValues)) {
			return;
		}

		const next = validateFormValues(
			formatResponse(query?.data ?? {}),
			sourceScenarioData,
		);

		for (const type of ['deposit', 'withdraw']) {
			if (!next?.[type]) {
				continue;
			}

			for (const date in next?.[type]?.data ?? {}) {
				for (const status in next?.[type]?.data?.[date]) {
					const path = [type, 'data', date, status].join('.');

					if (!getValues(path) || getValues(path)?.$initial) {
						setValue(path, next?.[type]?.data?.[date]?.[status]);
					}
				}
			}
		}
	}, [getValues, query?.data, setValue, sourceScenarioData]);
}

/**
 * @type { React.FC<{ history: unknown, yearHistory: unknown, forecast: unknown }> }
 */
const FormUpdater = React.memo(function FormUpdater(props) {
	const { history, yearHistory, forecast } = props;

	const { getValues, reset } = useFormContext();

	const sourceScenarioData = useSelector(
		ScenarioStore.selectors.sourceScenarioData,
		isEqual,
	);

	React.useEffect(() => {
		if (
			isEmptyData(history?.data) ||
			isEmptyData(yearHistory?.data) ||
			isEmptyData(forecast?.data) ||
			!isEmptyForm(getValues)
		) {
			return;
		}

		const data = merge(
			{},
			formatResponse(history?.data ?? {}),
			formatResponse(yearHistory?.data ?? {}),
			formatResponse(forecast?.data ?? {}),
		);

		const next = validateFormValues(data, sourceScenarioData);

		reset(next, {
			keepDefaultValues: false,
			keepValues: false,
			keepDirty: false,
			keepDirtyValues: false,
			keepErrors: false,
			keepTouched: false,
			keepSubmitCount: false,
			keepIsSubmitSuccessful: false,
			keepIsValid: false,
		});
	}, [
		forecast?.data,
		getValues,
		history?.data,
		reset,
		sourceScenarioData,
		yearHistory?.data,
	]);

	useUpdate(history);
	useUpdate(yearHistory);
	useUpdate(forecast);

	return null;
});

FormUpdater.propTypes = {
	history: PropTypes.object,
	yearHistory: PropTypes.object,
	forecast: PropTypes.object,
};

export default FormUpdater;
